import { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Logo } from '../components/Logo';
import { Loader2, ShieldAlert, Mail, ArrowRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AccountDeletionRequestPage() {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter the email address on your account.');
      return;
    }
    if (!confirm) {
      toast.error('Please tick the confirmation checkbox to submit your request.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/deletion-request`, {
        email: email.trim(),
        fullName: fullName.trim() || undefined,
        reason: reason.trim() || undefined,
        confirm: true,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not submit your request. Please try again or email support@shiftcover365.co.uk.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="account-deletion-page">
      {/* Header */}
      <header className="bg-[#0f1b3d] text-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="sm" variant="light" linkTo="/" />
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-[12.5px] text-white/70 hover:text-white transition-colors"
            data-testid="deletion-back-home"
          >
            Back to home <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-start sm:items-center justify-center p-4 sm:p-6 py-10">
        <Card className="w-full max-w-lg border-0 shadow-lg">
          <CardContent className="p-7 sm:p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-11 w-11 bg-red-50 rounded-xl flex items-center justify-center">
                <ShieldAlert className="h-6 w-6 text-[#DA291C]" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Account Deletion Request</h1>
                <p className="text-[13px] text-slate-500">Request deletion of your ShiftCover365 account.</p>
              </div>
            </div>

            {submitted ? (
              <div className="text-center py-6" data-testid="deletion-confirmation">
                <div className="mx-auto h-14 w-14 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-7 w-7 text-emerald-700" />
                </div>
                <h2 className="text-lg font-bold text-slate-900 mb-2">Request received</h2>
                <p className="text-[13.5px] text-slate-600 leading-relaxed mb-4">
                  Our team will action your request within one month, in line with UK GDPR. You&apos;ll receive a confirmation email at the address you provided once the request has been processed.
                </p>
                <p className="text-[12px] text-slate-400">
                  For urgent queries, email <a href="mailto:support@shiftcover365.co.uk" className="text-[#4338CA] hover:underline">support@shiftcover365.co.uk</a>.
                </p>
                <Link to="/" className="block mt-6">
                  <Button variant="outline" className="rounded-full h-11 px-6" data-testid="deletion-back-home-btn">
                    Back to home
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 mb-5 text-[12.5px] text-amber-900 leading-relaxed">
                  <p className="font-semibold mb-1">Before you continue</p>
                  <p>
                    If you&apos;re signed in you can also delete your account directly from your profile. Operational records (shifts, ratings, till logs, payments) may be retained where required by law — see our <Link to="/privacy" className="underline font-medium">Privacy Policy</Link> for detail.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="deletion-request-form">
                  <div className="space-y-1.5">
                    <Label htmlFor="del-email" className="text-slate-700 font-medium">Account email *</Label>
                    <Input
                      id="del-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="h-12 rounded-xl"
                      data-testid="deletion-email-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="del-name" className="text-slate-700 font-medium">Full name <span className="text-slate-400 font-normal">(optional)</span></Label>
                    <Input
                      id="del-name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="As shown on your account"
                      className="h-12 rounded-xl"
                      data-testid="deletion-name-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="del-reason" className="text-slate-700 font-medium">Reason <span className="text-slate-400 font-normal">(optional)</span></Label>
                    <Textarea
                      id="del-reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value.slice(0, 500))}
                      placeholder="Help us understand why you'd like your account deleted"
                      rows={3}
                      className="rounded-xl"
                      data-testid="deletion-reason-input"
                    />
                    <p className="text-[11px] text-slate-400 text-right">{reason.length}/500</p>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="deletion-confirm-row">
                    <input
                      type="checkbox"
                      checked={confirm}
                      onChange={(e) => setConfirm(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-[#DA291C] focus:ring-[#DA291C] shrink-0"
                      data-testid="deletion-confirm-checkbox"
                      required
                    />
                    <span className="text-[12.5px] text-slate-700 leading-relaxed">
                      I confirm I&apos;m requesting deletion of the account linked to the email above and I understand operational records may be retained where required by law.
                    </span>
                  </label>

                  <Button
                    type="submit"
                    disabled={submitting || !email.trim() || !confirm}
                    className="w-full h-12 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white font-semibold mt-2 disabled:opacity-60"
                    data-testid="deletion-submit-btn"
                  >
                    {submitting ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting...</>
                    ) : (
                      'Submit deletion request'
                    )}
                  </Button>
                  <p className="text-[11.5px] text-slate-400 text-center mt-1">
                    Or email <a href="mailto:support@shiftcover365.co.uk" className="text-[#4338CA] hover:underline inline-flex items-center gap-1"><Mail className="h-3 w-3" />support@shiftcover365.co.uk</a>
                  </p>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
